// src/component/CommunityFeed.js
import React, { useContext, useEffect, useMemo, useState, useRef } from "react";
import {
  Box,
  Stack,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Avatar,
  IconButton,
  TextField,
  Button,
  Divider,
  ImageList,
  ImageListItem,
  CircularProgress,
  Link as MuiLink,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItemIcon,
  useMediaQuery,
  Chip,
  Slide,
} from "@mui/material";

import FavoriteBorderRoundedIcon from "@mui/icons-material/FavoriteBorderRounded";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import KeyboardArrowLeftRoundedIcon from "@mui/icons-material/KeyboardArrowLeftRounded";
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded";

import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

import { AppThemeContext } from "./AppShell";
import { useAuth } from "../context/auth";
import { db } from "../api/firebase";

import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  addDoc,
  doc,
  updateDoc,
  serverTimestamp,
  increment,
  deleteField,
  getDoc,
  getDocs,
  deleteDoc,
  startAfter,
  where,
} from "firebase/firestore";

import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";

import { Snackbar, Alert } from "@mui/material";

// 一覧表示用画像の最大高さ（px）
const IMAGE_MAX_HEIGHT = 120;

// 1〜2枚のときの大きめ高さ
export const IMAGE_LARGE_HEIGHT = 176;

// 1ページあたり件数
const PAGE_SIZE = 20;

// プロフィール「最近の投稿」：1ページ件数
const RECENT_PAGE_SIZE = 10;

// ─────────────────────────────────────────────
// URL 抽出・リンク化
// ─────────────────────────────────────────────
const URL_REGEX = /(https?:\/\/[^\s　]+)/i;

function extractFirstUrl(text) {
  const match = text.match(URL_REGEX);
  return match ? match[0] : null;
}

function renderTextWithLinks(text) {
  if (!text) return null;
  const parts = text.split(URL_REGEX);

  return parts.map((part, idx) => {
    if (URL_REGEX.test(part)) {
      return (
        <MuiLink
          key={idx}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          underline="always"
          sx={{ wordBreak: "break-all" }}
        >
          {part}
        </MuiLink>
      );
    }
    return (
      <span key={idx} style={{ whiteSpace: "pre-wrap" }}>
        {part}
      </span>
    );
  });
}

// ─────────────────────────────────────────────
// 画像圧縮ユーティリティ（だいたい 100KB 目安）
// ─────────────────────────────────────────────
async function compressImageFile(
  file,
  maxSizeKB = 100,
  maxWidth = 1600,
  maxHeight = 1600
) {
  if (!file || !file.type || !file.type.startsWith("image/")) return file;

  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const img = await new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = dataUrl;
  });

  let { width, height } = img;
  const ratio = Math.min(maxWidth / width, maxHeight / height, 1);
  width = Math.round(width * ratio);
  height = Math.round(height * ratio);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, width, height);

  let quality = 0.9;
  const targetBytes = maxSizeKB * 1024;

  let blob = await new Promise((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", quality)
  );
  if (!blob) return file;

  while (blob.size > targetBytes && quality > 0.3) {
    quality -= 0.1;
    // eslint-disable-next-line no-await-in-loop
    blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", quality)
    );
    if (!blob) break;
  }

  return new File([blob], file.name.replace(/\.\w+$/, ".jpg"), {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
}

/* ==========================================================
      ⏱ 日付フォーマット / URLヘルパー
   ========================================================== */
function formatTimestamp(ts) {
  if (!ts) return "";
  let d;
  if (typeof ts.toDate === "function") {
    d = ts.toDate();
  } else if (ts instanceof Date) {
    d = ts;
  } else if (typeof ts === "string") {
    d = new Date(ts);
  } else {
    return "";
  }

  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return "たった今";
  if (diffMin < 60) return `${diffMin}分前`;
  if (diffHour < 24) return `${diffHour}時間前`;
  if (diffDay < 7) return `${diffDay}日前`;

  const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
  return `${d.getMonth() + 1}/${d.getDate()} ${pad(d.getHours())}:${pad(
    d.getMinutes()
  )}`;
}

function safeHostnameFromUrl(url) {
  try {
    const u = new URL(url);
    return u.hostname;
  } catch {
    return url;
  }
}

/* ==========================================================
    🖼 LazyImage（画面に入ったらロード）
   ========================================================== */
function LazyImage({ src, alt = "", style, className, onClick, maxHeight }) {
  const imgRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = imgRef.current;
    if (!el) return;

    // 対応ブラウザでは基本 loading="lazy" に任せる
    if ("loading" in HTMLImageElement.prototype) {
      setVisible(true);
      return;
    }

    // IntersectionObserver で viewport 近くでロード
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      {
        root: null,
        rootMargin: "200px",
        threshold: 0.01,
      }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <img
      ref={imgRef}
      src={visible ? src : undefined}
      data-src={src}
      loading="lazy"
      alt={alt}
      onClick={onClick}
      className={className}
      style={{
        ...style,
        maxHeight: maxHeight ?? style?.maxHeight,
        objectFit: style?.objectFit || "cover",
        display: "block",
      }}
    />
  );
}

function getImageMaxHeight(count) {
  if (count <= 2) return IMAGE_LARGE_HEIGHT;
  return IMAGE_MAX_HEIGHT;
}

/* ==========================================================
    📱 メインのタイムラインコンポーネント
   ========================================================== */
export default function CommunityFeed() {
  const theme = useContext(AppThemeContext) || {};
  const accent = theme.railActive || "#5A35FF";
  const surface = theme.surfaceBg || "#FFFFFF";
  const textBase = theme.railText || "rgba(30,30,60,0.9)";

  const { user } = useAuth();

  const [userProfile, setUserProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  // 投稿フォーム
  const [postText, setPostText] = useState("");
  const [postFiles, setPostFiles] = useState([]); // { file, previewUrl }[]
  const [submittingPost, setSubmittingPost] = useState(false);

  // 詳細ダイアログ
  const [detailPost, setDetailPost] = useState(null);

  // 共通 Lightbox
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerSlides, setViewerSlides] = useState([]);
  const [viewerIndex, setViewerIndex] = useState(0);

  // Snackbar
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // ページング
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef(null);

  // プロフィールダイアログ
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileUser, setProfileUser] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  // users/{uid} から自分のプロフィール
  useEffect(() => {
    if (!user) {
      setUserProfile(null);
      return;
    }
    const loadProfile = async () => {
      try {
        const ref = doc(db, "users", user.uid);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          setUserProfile({ id: user.uid, ...snap.data() });
        } else {
          setUserProfile(null);
        }
      } catch (e) {
        console.error("Failed to load user profile:", e);
      }
    };
    loadProfile();
  }, [user]);

  // 投稿一覧（初回 PAGE_SIZE 件だけ取得。以後は明示的な再読み込みまで変えない）
  useEffect(() => {
    let cancelled = false;

    const fetchInitial = async () => {
      if (!user) {
        setPosts([]);
        setLastVisible(null);
        setHasMore(false);
        setLoading(false);
        return;
      }

      setLoading(true);
      setPosts([]);
      setLastVisible(null);
      setHasMore(false);

      try {
        const postsRef = collection(db, "mmPosts");
        const q = query(
          postsRef,
          orderBy("createdAt", "desc"),
          limit(PAGE_SIZE)
        );

        const snap = await getDocs(q);
        if (cancelled) return;

        const list = snap.docs.map((d) => {
          const data = d.data();
          const likes = data.likes || {};
          const isLiked = user ? !!likes[user.uid] : false;
          return {
            id: d.id,
            ...data,
            isLiked,
          };
        });

        setPosts(list);

        const lastDoc = snap.docs[snap.docs.length - 1] || null;
        setLastVisible(lastDoc);
        setHasMore(snap.size === PAGE_SIZE);
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load posts:", err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchInitial();

    return () => {
      cancelled = true;
    };
  }, [user]);

  const loadMore = async () => {
    if (!lastVisible || !hasMore || loadingMore) return;

    setLoadingMore(true);
    try {
      const postsRef = collection(db, "mmPosts");
      const q = query(
        postsRef,
        orderBy("createdAt", "desc"),
        startAfter(lastVisible),
        limit(PAGE_SIZE)
      );

      const snap = await getDocs(q);
      const list = snap.docs.map((d) => {
        const data = d.data();
        const likes = data.likes || {};
        const isLiked = user ? !!likes[user.uid] : false;
        return {
          id: d.id,
          ...data,
          isLiked,
        };
      });

      setPosts((prev) => [...prev, ...list]);

      const last = snap.docs[snap.docs.length - 1] || null;
      setLastVisible(last);
      setHasMore(snap.size === PAGE_SIZE);
    } catch (err) {
      console.error("Failed to load more posts:", err);
    } finally {
      setLoadingMore(false);
    }
  };

  // 無限スクロール用 IntersectionObserver
  useEffect(() => {
    const target = sentinelRef.current;
    if (!target) return;
    if (!hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.9 }
    );

    observer.observe(target);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, sentinelRef.current]);

  // プロフィールを開く（一覧などから）
  const handleOpenProfile = async (userId, fallback = {}) => {
    if (!userId) return;

    // まずは fallback を即時反映
    setProfileUser((prev) => ({
      id: userId,
      userName: fallback.userName ?? prev?.userName,
      avatar: fallback.avatar ?? prev?.avatar,
    }));
    setProfileOpen(true);
    setProfileLoading(true);

    try {
      const ref = doc(db, "users", userId);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data();
        setProfileUser({
          id: userId,
          ...data,
          userName:
            data.name ||
            data.registrationName ||
            fallback.userName ||
            data.userName,
          avatar: data.avatar || fallback.avatar,
        });
      } else {
        setProfileUser((prev) => ({
          ...prev,
          userName:
            fallback.userName ??
            prev?.userName ??
            "プロフィール情報が登録されていません",
        }));
      }
    } catch (e) {
      console.error("Failed to load profile:", e);
    } finally {
      setProfileLoading(false);
    }
  };

  // 詳細ダイアログからプロフィールを開くときは、
  // いったん詳細を閉じてからプロフィールだけ表示する
  const handleOpenProfileFromDetail = (userId, fallback = {}) => {
    setDetailPost(null);
    // 次のレンダリングサイクルでプロフィールを開く
    setTimeout(() => {
      handleOpenProfile(userId, fallback);
    }, 0);
  };

  const handleCloseProfile = () => {
    setProfileOpen(false);
  };

  const postTextCount = postText.length;
  const postTextOver = postTextCount > 100;

  const handleSelectPostFiles = (e) => {
    const files = Array.from(e.target.files || []);
    const imageFiles = files.filter((f) => f.type.startsWith("image/"));
    const selected = imageFiles.slice(0, 4);
    const withPreview = selected.map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setPostFiles(withPreview);
  };

  const movePostFile = (index, direction) => {
    setPostFiles((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      const tmp = next[index];
      next[index] = next[target];
      next[target] = tmp;
      return next;
    });
  };

  const handleCreatePost = async () => {
    if (!user) return;
    if (!postText.trim() && postFiles.length === 0) return;
    if (postTextOver) return;

    setSubmittingPost(true);

    try {
      const firstUrl = extractFirstUrl(postText);

      const now = new Date();
      const expiresAt = new Date(
        now.getTime() + 365 * 24 * 60 * 60 * 1000
      ).toISOString();

      const postsRef = collection(db, "mmPosts");

      const displayName =
        userProfile?.name ||
        userProfile?.registrationName ||
        user?.displayName ||
        "名無し";

      const avatarUrl = userProfile?.avatar || user?.photoURL || "";

      const postRef = await addDoc(postsRef, {
        userId: user.uid,
        userName: displayName,
        userAvatar: avatarUrl,
        text: postText.trim(),
        imageUrls: [],
        linkUrl: firstUrl || null,
        linkMeta: null,
        likeCount: 0,
        likes: {},
        commentCount: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      if (postFiles.length > 0) {
        const storage = getStorage();
        const uploaded = [];

        for (let i = 0; i < postFiles.length; i++) {
          const originalFile = postFiles[i].file;
          // eslint-disable-next-line no-await-in-loop
          const compressedFile = await compressImageFile(originalFile, 100);

          const path = `communityPosts/${postRef.id}/${now.getTime()}_${i}_${
            compressedFile.name
          }`;
          const ref = storageRef(storage, path);

          // eslint-disable-next-line no-await-in-loop
          await uploadBytes(ref, compressedFile, {
            contentType: compressedFile.type || "image/jpeg",
            customMetadata: {
              createdAt: now.toISOString(),
              expiresAt,
            },
          });

          // eslint-disable-next-line no-await-in-loop
          const url = await getDownloadURL(ref);
          uploaded.push({ url, path, expiresAt });
        }

        await updateDoc(postRef, {
          imageUrls: uploaded,
          updatedAt: serverTimestamp(),
        });
      }

      setPostText("");
      setPostFiles([]);
      setSnackbarMessage("投稿が完了しました");
    } catch (err) {
      console.error("Failed to create post:", err);
    } finally {
      setSubmittingPost(false);
    }
  };

  // いいねトグル
  const handleTogglePostLike = async (postId) => {
    if (!user) return;
    const current = posts.find((p) => p.id === postId);
    if (!current) return;

    const newIsLiked = !current.isLiked;
    const delta = newIsLiked ? 1 : -1;

    // ローカル即時反映
    setPosts((prev) =>
      prev.map((p) =>
        p.id === postId
          ? {
              ...p,
              isLiked: newIsLiked,
              likeCount: (p.likeCount || 0) + delta,
            }
          : p
      )
    );

    // Firestore 更新
    try {
      const postRef = doc(db, "mmPosts", postId);
      const field = `likes.${user.uid}`;

      if (newIsLiked) {
        await updateDoc(postRef, {
          [field]: true,
          likeCount: increment(1),
        });
      } else {
        await updateDoc(postRef, {
          [field]: deleteField(),
          likeCount: increment(-1),
        });
      }
    } catch (err) {
      console.error("Failed to toggle like:", err);
    }
  };

  const handleOpenDetail = (post) => {
    window.history.pushState({ postDetail: true }, "");
    setDetailPost(post);
  };

  const handleCloseDetail = () => {
    // 「＜」やUIボタンで閉じた場合は pushState で追加した履歴を戻す
    if (detailPost) {
      window.history.back();
      return; // popstate イベントで setDetailPost(null) が呼ばれる
    }
  };

  // ブラウザバックで詳細ダイアログを閉じる
  useEffect(() => {
    if (!detailPost) return;

    const onPopState = () => {
      setDetailPost(null);
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [detailPost]);

  const handleOpenViewer = (imageUrls, index) => {
    if (!imageUrls || imageUrls.length === 0) return;
    const slides = imageUrls.map((url) => ({ src: url }));
    setViewerSlides(slides);
    setViewerIndex(index);
    setViewerOpen(true);
  };

  const handleCloseViewer = () => {
    setViewerOpen(false);
  };

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        bgcolor: surface,
        color: textBase,
        position: "relative",
      }}
    >
      {/* ヘッダー */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          bgcolor: surface,
          borderBottom: `1px solid ${theme.bottomBorder || "rgba(0,0,0,0.06)"}`,
          px: 2,
          py: 1.5,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 800, color: accent }}>
          マスターマインド・タイムライン
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.7 }}>
          テキスト100字・画像4枚までのミニ投稿
        </Typography>
      </Box>

      <Box sx={{ p: 0, pb: 8, maxWidth: 720, mx: "auto" }}>
        {/* 投稿フォーム */}
        <Card
          elevation={0}
          sx={{
            mb: 2,
            borderRadius: 0,
            border: "1px solid rgba(0,0,0,0.06)",
          }}
        >
          <CardContent sx={{ pb: 1.5 }}>
            <Stack direction="row" spacing={2}>
              <Avatar
                src={userProfile?.avatar || user?.photoURL || ""}
                sx={{ width: 40, height: 40 }}
              >
                {(
                  userProfile?.name ||
                  userProfile?.registrationName ||
                  user?.displayName ||
                  "？"
                ).charAt(0)}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <TextField
                  multiline
                  fullWidth
                  minRows={2}
                  maxRows={4}
                  placeholder="いまの気持ちをシェア（100文字まで）"
                  value={postText}
                  onChange={(e) => setPostText(e.target.value)}
                  inputProps={{ maxLength: 200 }}
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "透明",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "transparent",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "transparent",
                    },
                    bgcolor: theme.appBg || "rgba(0,0,0,0.02)",
                    borderRadius: 2,
                    px: 1.5,
                    py: 0.5,
                  }}
                />

                {/* 画像ボタン + プレビュー + 文字数 + 投稿 */}
                <Stack sx={{ mt: 1, width: "100%" }} spacing={1}>
                  {/* 1行目：画像ボタン */}
                  <Button
                    component="label"
                    variant="text"
                    size="small"
                    startIcon={<ImageRoundedIcon />}
                    sx={{ alignSelf: "flex-start" }}
                  >
                    画像
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      multiple
                      onChange={handleSelectPostFiles}
                    />
                  </Button>

                  {/* 2行目：画像プレビュー */}
                  {postFiles.length > 0 && (
                    <Box
                      sx={{
                        width: "100%",
                        maxWidth: "100%",
                        overflowX: "auto",
                        overflowY: "hidden",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        p: 0.5,
                        borderRadius: 1.5,
                        bgcolor: "rgba(0,0,0,0.02)",
                      }}
                    >
                      {postFiles.map((pf, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            position: "relative",
                            width: 64,
                            height: 64,
                            borderRadius: 1.5,
                            overflow: "hidden",
                            border: "1px solid rgba(0,0,0,0.08)",
                            flexShrink: 0,
                          }}
                        >
                          <img
                            src={pf.previewUrl}
                            alt={`preview-${idx}`}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              display: "block",
                            }}
                          />
                          <Stack
                            direction="row"
                            spacing={0.2}
                            sx={{
                              position: "absolute",
                              right: 2,
                              bottom: 2,
                            }}
                          >
                            <IconButton
                              size="small"
                              sx={{
                                width: 20,
                                height: 20,
                                bgcolor: "rgba(0,0,0,0.45)",
                                "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                              }}
                              disabled={idx === 0}
                              onClick={() => movePostFile(idx, -1)}
                            >
                              <KeyboardArrowLeftRoundedIcon
                                sx={{ fontSize: 14, color: "#fff" }}
                              />
                            </IconButton>
                            <IconButton
                              size="small"
                              sx={{
                                width: 20,
                                height: 20,
                                bgcolor: "rgba(0,0,0,0.45)",
                                "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                              }}
                              disabled={idx === postFiles.length - 1}
                              onClick={() => movePostFile(idx, 1)}
                            >
                              <KeyboardArrowRightRoundedIcon
                                sx={{ fontSize: 14, color: "#fff" }}
                              />
                            </IconButton>
                          </Stack>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* 3行目：文字数 + 投稿ボタン */}
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="flex-end"
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: postTextOver ? "error.main" : "text.secondary",
                      }}
                    >
                      {postTextCount}/100
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      endIcon={<SendRoundedIcon />}
                      disabled={
                        submittingPost ||
                        (!postText.trim() && postFiles.length === 0) ||
                        postTextOver
                      }
                      onClick={handleCreatePost}
                    >
                      投稿
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* 投稿一覧 */}
        {loading ? (
          <Box sx={{ textAlign: "center", mt: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : posts.length === 0 ? (
          <Box sx={{ textAlign: "center", mt: 4 }}>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              まだ投稿はありません。最初の一言を投稿してみましょう。
            </Typography>
          </Box>
        ) : (
          <>
            <Stack spacing={0}>
              {posts.map((post) => (
                <PostCard
                  key={post.id}
                  post={post}
                  currentUser={user}
                  theme={theme}
                  userProfile={userProfile}
                  onToggleLike={() => handleTogglePostLike(post.id)}
                  onOpenDetail={() => handleOpenDetail(post)}
                  onOpenViewer={handleOpenViewer}
                  onDeleted={() => setSnackbarMessage("投稿を削除しました")}
                  onOpenProfile={(userId, fallback) =>
                    handleOpenProfile(userId, fallback)
                  }
                />
              ))}
            </Stack>

            {/* 読み込み用 sentinel */}
            <Box ref={sentinelRef} sx={{ height: hasMore ? 32 : 0 }} />

            {/* 追加読み込み中インジケータ */}
            {loadingMore && (
              <Box sx={{ textAlign: "center", py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {/* 読み切ったメッセージ */}
            {!hasMore && !loadingMore && posts.length > 0 && (
              <Box sx={{ textAlign: "center", py: 1 }}>
                <Typography variant="caption" sx={{ opacity: 0.6 }}>
                  すべて読み込みました
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* 投稿中オーバーレイ */}
      {submittingPost && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 2000,
            bgcolor: "rgba(0,0,0,0.45)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={40} sx={{ color: "#fff" }} />
          <Typography
            variant="body1"
            sx={{ mt: 2, color: "#fff", fontWeight: 700 }}
          >
            投稿しています…
          </Typography>
          {postFiles.length > 0 && (
            <Typography
              variant="body2"
              sx={{ mt: 1, color: "#fff", opacity: 0.8 }}
            >
              画像をアップロード中です。画面を閉じずにお待ちください。
            </Typography>
          )}
        </Box>
      )}

      {/* 詳細ダイアログ */}
      {detailPost && (
        <PostDetailDialog
          open={Boolean(detailPost)}
          onClose={handleCloseDetail}
          post={detailPost}
          currentUser={user}
          theme={theme}
          userProfile={userProfile}
          onToggleLike={() => handleTogglePostLike(detailPost.id)}
          onOpenViewer={handleOpenViewer}
          onCommentDeleted={() => setSnackbarMessage("コメントを削除しました")}
          onOpenProfile={(userId, fallback) =>
            handleOpenProfileFromDetail(userId, fallback)
          }
        />
      )}

      {/* 共通 Lightbox */}
      <Lightbox
        open={viewerOpen}
        close={handleCloseViewer}
        slides={viewerSlides}
        index={viewerIndex}
        carousel={{ finite: true }}
        currentUser={user} // ★ 追加
        currentUserProfile={userProfile} // ★ 追加
      />

      {/* プロフィールドロワー */}
      <ProfileDialog
        open={profileOpen}
        onClose={handleCloseProfile}
        user={profileUser}
        loading={profileLoading}
        theme={theme}
        currentUser={user}
        currentUserProfile={userProfile}
        onOpenViewer={handleOpenViewer}
      />

      <Snackbar
        open={Boolean(snackbarMessage)}
        autoHideDuration={3000}
        onClose={() => setSnackbarMessage("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSnackbarMessage("")}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

/* ==========================================================
    🧩 投稿カード（サマリ表示）
   ========================================================== */
function PostCard({
  post,
  currentUser,
  theme,
  userProfile,
  onToggleLike,
  onOpenDetail,
  onOpenViewer,
  onDeleted,
  onOpenProfile,
}) {
  const surface = theme.surfaceBg || "#FFFFFF";
  const accent = theme.railActive || "#5A35FF";

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isMine = currentUser && post.userId === currentUser.uid;

  const imageUrls = useMemo(() => {
    if (!Array.isArray(post.imageUrls)) return [];
    return post.imageUrls.map((img) =>
      typeof img === "string" ? img : img.url
    );
  }, [post.imageUrls]);

  const showLinkPreview =
    post.linkUrl && post.linkMeta && post.linkMeta.imageUrl;
  const showImages = !showLinkPreview && imageUrls.length > 0;

  const isDesktop = useMediaQuery("(min-width:900px)");

  const count = imageUrls.length;
  const maxH = getImageMaxHeight(count);
  const cols = (() => {
    if (count <= 1) return 1;
    if (count === 2) return 2;
    if (count === 3) return isDesktop ? 3 : 2;
    if (count === 4) return 2;
    return 2;
  })();

  // ★ クリック貫通対策：モーダル/メニューが開いている間は Card click を無効化
  const isModalBlocking = Boolean(menuAnchor) || deleteDialogOpen || deleting;

  const stopEvent = (e) => {
    if (!e) return;
    e.stopPropagation();
    e.preventDefault?.();
    // iOS系の「貫通クリック」保険
    if (e.nativeEvent?.stopImmediatePropagation) {
      e.nativeEvent.stopImmediatePropagation();
    }
  };

  const handleOpenMenu = (e) => {
    stopEvent(e);
    setMenuAnchor(e.currentTarget);
  };

  const handleCloseMenu = (e) => {
    if (e) stopEvent(e);
    setMenuAnchor(null);
  };

  const handleClickDeleteMenu = (e) => {
    if (e) stopEvent(e);
    setDeleteDialogOpen(true);
    setMenuAnchor(null);
  };

  const handleCancelDelete = (e) => {
    if (e) stopEvent(e);
    if (deleting) return;
    setDeleteDialogOpen(false);
  };

  const handleConfirmDelete = async (e) => {
    if (e) stopEvent(e);
    if (!currentUser || !isMine) return;

    setDeleting(true);
    try {
      const storage = getStorage();

      // 投稿画像削除
      if (Array.isArray(post.imageUrls)) {
        for (const img of post.imageUrls) {
          const path = typeof img === "string" ? null : img.path;
          if (!path) continue;
          try {
            // eslint-disable-next-line no-await-in-loop
            await deleteObject(storageRef(storage, path));
          } catch (err) {
            console.error("Failed to delete post image:", path, err);
          }
        }
      }

      // コメント画像削除
      const commentsRef = collection(db, "mmPosts", post.id, "comments");
      const commentsSnap = await getDocs(commentsRef);

      for (const docSnap of commentsSnap.docs) {
        const cData = docSnap.data();
        if (Array.isArray(cData.imageUrls)) {
          for (const img of cData.imageUrls) {
            const path = typeof img === "string" ? null : img.path;
            if (!path) continue;
            try {
              // eslint-disable-next-line no-await-in-loop
              await deleteObject(storageRef(storage, path));
            } catch (err) {
              console.error("Failed to delete comment image:", path, err);
            }
          }
        }
        // eslint-disable-next-line no-await-in-loop
        await deleteDoc(docSnap.ref);
      }

      // 投稿ドキュメント削除
      await deleteDoc(doc(db, "mmPosts", post.id));
    } catch (err) {
      console.error("Failed to delete post:", err);
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setMenuAnchor(null);
      if (onDeleted) onDeleted();
    }
  };

  const handleCardClick = (e) => {
    // ★ ここが要：ダイアログ/メニュー中は詳細を開かない
    if (isModalBlocking) {
      stopEvent(e);
      return;
    }
    if (onOpenDetail) onOpenDetail();
  };

  const handleImageClick = (e, index) => {
    stopEvent(e);
    if (onOpenViewer) onOpenViewer(imageUrls, index);
  };

  const handleLikeClick = (e) => {
    stopEvent(e);
    if (onToggleLike) onToggleLike();
  };

  const handleCommentIconClick = (e) => {
    stopEvent(e);
    if (onOpenDetail) onOpenDetail();
  };

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 0,
        borderBottom: "1px solid rgba(0,0,0,0.05)",
        bgcolor: surface,
        cursor: "pointer",
        "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
      }}
      onClick={handleCardClick}
    >
      {/* 削除メニュー */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            minWidth: 160,
            borderRadius: 2,
            boxShadow: "0 10px 30px rgba(15,10,40,0.25)",
          },
        }}
      >
        <MenuItem onClick={handleClickDeleteMenu}>
          <ListItemIcon>
            <DeleteOutlineRoundedIcon fontSize="small" />
          </ListItemIcon>
          投稿を削除
        </MenuItem>
      </Menu>

      {/* 削除確認ダイアログ */}
      <Dialog
        open={deleteDialogOpen}
        fullWidth
        maxWidth="xs"
        onClose={(e) => {
          // 外側タップ/ESC でも閉じる運用は維持しつつ、貫通を止める
          handleCancelDelete(e);
        }}
        // MUI v5: slotProps が使える前提
        slotProps={{
          backdrop: {
            onClick: (e) => {
              // ★ ここが効く：backdrop の click/touch を背面へ流さない
              stopEvent(e);
            },
          },
        }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningAmberRoundedIcon
            sx={{ color: "warning.main", fontSize: 24 }}
          />
          この投稿を削除しますか？
        </DialogTitle>

        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            この操作は取り消せません。消去した投稿はもとに戻せません。
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.75 }}>
            投稿と、その投稿に添付された画像・コメント画像はすべて削除されます。
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCancelDelete} disabled={deleting}>
            キャンセル
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? "削除中..." : "削除する"}
          </Button>
        </DialogActions>
      </Dialog>

      <CardHeader
        avatar={
          <Avatar
            src={post.userAvatar || ""}
            sx={{ px: 0 }}
            onClick={(e) => {
              stopEvent(e);
              if (onOpenProfile) {
                onOpenProfile(post.userId, {
                  userName: post.userName,
                  avatar: post.userAvatar,
                });
              }
            }}
          >
            {(post.userName || "？").charAt(0)}
          </Avatar>
        }
        title={
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, lineHeight: 1.1, mb: 0.2 }}
          >
            {post.userName || "名無し"}
          </Typography>
        }
        subheader={
          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            {formatTimestamp(post.createdAt)}
          </Typography>
        }
        action={
          isMine && (
            <IconButton
              size="small"
              onClick={handleOpenMenu}
              aria-label="post menu"
            >
              <MoreVertRoundedIcon fontSize="small" />
            </IconButton>
          )
        }
        sx={{ py: 0 }}
      />

      <CardContent sx={{ pt: 0, pb: 0 }}>
        {/* 本文 */}
        {post.text && (
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {renderTextWithLinks(post.text)}
          </Typography>
        )}

        {/* URL プレビュー */}
        {showLinkPreview && (
          <Box
            component={MuiLink}
            href={post.linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            underline="none"
            onClick={(e) => stopEvent(e)}
            sx={{
              display: "block",
              borderRadius: 2,
              overflow: "hidden",
              border: "1px solid rgba(0,0,0,0.08)",
              mb: 1.5,
            }}
          >
            <Stack direction="row" spacing={0}>
              <Box
                component="img"
                src={post.linkMeta.imageUrl}
                alt={post.linkMeta.title || post.linkUrl}
                sx={{
                  width: 96,
                  height: 96,
                  objectFit: "cover",
                  flexShrink: 0,
                }}
              />
              <Box sx={{ p: 1.2, flex: 1, minWidth: 0 }}>
                {post.linkMeta.title && (
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, mb: 0.5 }}
                    noWrap
                  >
                    {post.linkMeta.title}
                  </Typography>
                )}
                {post.linkMeta.description && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {post.linkMeta.description}
                  </Typography>
                )}
                <Typography
                  variant="caption"
                  sx={{ mt: 0.5, display: "block", opacity: 0.7 }}
                  noWrap
                >
                  {post.linkMeta.siteName || safeHostnameFromUrl(post.linkUrl)}
                </Typography>
              </Box>
            </Stack>
          </Box>
        )}

        {/* 画像サムネイル */}
        {showImages && (
          <ImageList
            cols={cols}
            gap={1}
            sx={{
              mb: 1.5,
              borderRadius: 0,
              overflow: "hidden",
              maxWidth: 600,
              mx: 0,
            }}
          >
            {imageUrls.map((url, idx) => (
              <ImageListItem key={idx}>
                <img
                  src={url}
                  alt={`post-${post.id}-${idx}`}
                  loading="lazy"
                  style={{
                    width: "100%",
                    height: "auto",
                    maxHeight: maxH,
                    objectFit: "cover",
                    display: "block",
                    cursor: "pointer",
                  }}
                  onClick={(e) => handleImageClick(e, idx)}
                />
              </ImageListItem>
            ))}
          </ImageList>
        )}

        {/* いいね / コメント数 */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton
            size="small"
            onClick={handleLikeClick}
            disabled={!currentUser}
          >
            {post.isLiked ? (
              <FavoriteRoundedIcon sx={{ color: accent }} fontSize="small" />
            ) : (
              <FavoriteBorderRoundedIcon fontSize="small" />
            )}
          </IconButton>
          <Typography variant="body2" sx={{ mr: 2 }}>
            {post.likeCount || 0}
          </Typography>

          <IconButton
            size="small"
            onClick={handleCommentIconClick}
            sx={{ mr: 0.5 }}
          >
            <ChatBubbleOutlineRoundedIcon fontSize="small" />
          </IconButton>
          <Typography variant="body2">{post.commentCount ?? 0}</Typography>
        </Stack>
      </CardContent>
    </Card>
  );
}

/* ==========================================================
    🧩 投稿詳細ダイアログ（コメント付き）
   ========================================================== */
function PostDetailDialog({
  open,
  onClose,
  post,
  currentUser,
  theme,
  userProfile,
  onToggleLike,
  onOpenViewer,
  onCommentDeleted,
  onOpenProfile,
}) {
  const accent = theme.railActive || "#5A35FF";
  const surface = theme.surfaceBg || "#FFFFFF";
  const isXs = useMediaQuery("(max-width:600px)");

  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(true);

  const [commentText, setCommentText] = useState("");
  const [commentFiles, setCommentFiles] = useState([]);
  const [submittingComment, setSubmittingComment] = useState(false);

  const commentTextCount = commentText.length;
  const commentTextOver = commentTextCount > 88;

  const [targetComment, setTargetComment] = useState(null);
  const [commentMenuAnchor, setCommentMenuAnchor] = useState(null);
  const [commentDeleteDialogOpen, setCommentDeleteDialogOpen] = useState(false);
  const [deletingComment, setDeletingComment] = useState(false);

  // ─────────────────────────────────────────────
  // 📱 右スワイプで閉じる（xs=fullScreen のみ）
  // ─────────────────────────────────────────────
  const swipeRef = useRef({
    active: false,
    startX: 0,
    startY: 0,
    startTime: 0,
  });

  const SWIPE_EDGE_PX = 24; // 左端からの開始のみ許可
  const SWIPE_MIN_DX = 80; // 右方向の最小移動
  const SWIPE_MAX_DY = 60; // 縦ブレ許容
  const SWIPE_MAX_MS = 900; // ダラダラは無効（任意）

  const onSwipePointerDown = (e) => {
    if (!isXs) return;
    if (submittingComment) return;
    if (e.pointerType === "mouse") return;

    const x = e.clientX ?? 0;
    const y = e.clientY ?? 0;

    if (x > SWIPE_EDGE_PX) return;

    swipeRef.current = {
      active: true,
      startX: x,
      startY: y,
      startTime: Date.now(),
    };
  };

  const onSwipePointerMove = () => {
    if (!swipeRef.current.active) return;
  };

  const onSwipePointerUp = (e) => {
    if (!swipeRef.current.active) return;

    const endX = e.clientX ?? 0;
    const endY = e.clientY ?? 0;
    const { startX, startY, startTime } = swipeRef.current;

    swipeRef.current.active = false;

    const dx = endX - startX;
    const dy = Math.abs(endY - startY);
    const ms = Date.now() - startTime;

    if (dx >= SWIPE_MIN_DX && dy <= SWIPE_MAX_DY && ms <= SWIPE_MAX_MS) {
      handleClose();
    }
  };

  const onSwipePointerCancel = () => {
    swipeRef.current.active = false;
  };

  // コメント購読
  useEffect(() => {
    if (!open || !post?.id) return;

    const commentsRef = collection(db, "mmPosts", post.id, "comments");
    const q = query(commentsRef, orderBy("createdAt", "desc"), limit(100));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => {
          const data = d.data();
          const likes = data.likes || {};
          const isLiked = currentUser ? !!likes[currentUser.uid] : false;
          return {
            id: d.id,
            ...data,
            isLiked,
          };
        });
        setComments(list);
        setLoadingComments(false);
      },
      (err) => {
        console.error("Failed to load comments:", err);
        setLoadingComments(false);
      }
    );

    return () => unsub();
  }, [open, post?.id, currentUser?.uid]);

  const commentCount = comments.length;

  const handleSelectCommentFiles = (e) => {
    const files = Array.from(e.target.files || []);
    const imageFiles = files.filter((f) => f.type.startsWith("image/"));
    const selected = imageFiles.slice(0, 4);
    const withPreview = selected.map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setCommentFiles(withPreview);
  };

  const moveCommentFile = (index, direction) => {
    setCommentFiles((prev) => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      const tmp = next[index];
      next[index] = next[target];
      next[target] = tmp;
      return next;
    });
  };

  const handleCreateComment = async () => {
    if (!currentUser) return;
    if (!commentText.trim() && commentFiles.length === 0) return;
    if (commentTextOver) return;

    setSubmittingComment(true);

    try {
      const now = new Date();
      const expiresAt = new Date(
        now.getTime() + 365 * 24 * 60 * 60 * 1000
      ).toISOString();

      const commentsRef = collection(db, "mmPosts", post.id, "comments");

      const displayName =
        userProfile?.name ||
        userProfile?.registrationName ||
        currentUser.displayName ||
        "名無し";

      const avatarUrl = userProfile?.avatar || currentUser.photoURL || "";

      const commentRef = await addDoc(commentsRef, {
        userId: currentUser.uid,
        userName: displayName,
        userAvatar: avatarUrl,
        text: commentText.trim(),
        imageUrls: [],
        likeCount: 0,
        likes: {},
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      if (commentFiles.length > 0) {
        const storage = getStorage();
        const uploaded = [];

        for (let i = 0; i < commentFiles.length; i++) {
          const originalFile = commentFiles[i].file;
          // eslint-disable-next-line no-await-in-loop
          const compressedFile = await compressImageFile(originalFile, 100);

          const path = `communityPosts/${post.id}/comments/${
            commentRef.id
          }/${now.getTime()}_${i}_${compressedFile.name}`;
          const ref = storageRef(storage, path);

          // eslint-disable-next-line no-await-in-loop
          await uploadBytes(ref, compressedFile, {
            contentType: compressedFile.type || "image/jpeg",
            customMetadata: {
              createdAt: now.toISOString(),
              expiresAt,
            },
          });

          // eslint-disable-next-line no-await-in-loop
          const url = await getDownloadURL(ref);
          uploaded.push({ url, path, expiresAt });
        }

        await updateDoc(commentRef, {
          imageUrls: uploaded,
          updatedAt: serverTimestamp(),
        });
      }

      await updateDoc(doc(db, "mmPosts", post.id), {
        commentCount: increment(1),
      });

      setCommentText("");
      setCommentFiles([]);
    } catch (err) {
      console.error("Failed to create comment:", err);
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleToggleCommentLike = async (comment) => {
    if (!currentUser) return;

    try {
      const commentRef = doc(db, "mmPosts", post.id, "comments", comment.id);
      const field = `likes.${currentUser.uid}`;

      if (comment.isLiked) {
        await updateDoc(commentRef, {
          [field]: deleteField(),
          likeCount: increment(-1),
        });
      } else {
        await updateDoc(commentRef, {
          [field]: true,
          likeCount: increment(1),
        });
      }
    } catch (err) {
      console.error("Failed to toggle comment like:", err);
    }
  };

  // コメント削除メニュー関連
  const handleOpenCommentMenu = (e, comment) => {
    e.stopPropagation();
    setTargetComment(comment);
    setCommentMenuAnchor(e.currentTarget);
  };

  const handleCloseCommentMenu = () => {
    setCommentMenuAnchor(null);
  };

  const handleOpenCommentDeleteDialog = () => {
    setCommentDeleteDialogOpen(true);
    handleCloseCommentMenu();
  };

  const handleCancelCommentDelete = (e) => {
    if (e) e.stopPropagation();
    if (deletingComment) return;
    setCommentDeleteDialogOpen(false);
    setTargetComment(null);
  };

  const handleConfirmCommentDelete = async (e) => {
    if (e) e.stopPropagation();
    if (!currentUser || !targetComment) return;
    if (targetComment.userId !== currentUser.uid) return;

    setDeletingComment(true);
    try {
      const storage = getStorage();

      // コメント画像削除
      if (Array.isArray(targetComment.imageUrls)) {
        for (const img of targetComment.imageUrls) {
          const path = typeof img === "string" ? null : img.path;
          if (!path) continue;
          try {
            // eslint-disable-next-line no-await-in-loop
            await deleteObject(storageRef(storage, path));
          } catch (err) {
            console.error("Failed to delete comment image:", path, err);
          }
        }
      }

      // Firestore コメント削除
      await deleteDoc(
        doc(db, "mmPosts", post.id, "comments", targetComment.id)
      );

      // 親投稿のコメント数を -1
      await updateDoc(doc(db, "mmPosts", post.id), {
        commentCount: increment(-1),
      });

      // ローカル state からも削除
      setComments((prev) => prev.filter((c) => c.id !== targetComment.id));

      if (onCommentDeleted) onCommentDeleted();
    } catch (err) {
      console.error("Failed to delete comment:", err);
    } finally {
      setDeletingComment(false);
      setCommentDeleteDialogOpen(false);
      setTargetComment(null);
    }
  };

  const imageUrls = useMemo(() => {
    if (!Array.isArray(post.imageUrls)) return [];
    return post.imageUrls.map((img) =>
      typeof img === "string" ? img : img.url
    );
  }, [post.imageUrls]);

  const showLinkPreview =
    post.linkUrl && post.linkMeta && post.linkMeta.imageUrl;
  const showImages = !showLinkPreview && imageUrls.length > 0;

  const isDesktop = useMediaQuery("(min-width:900px)");

  const count = imageUrls.length;
  const maxH = getImageMaxHeight(count);
  const cols = (() => {
    if (count <= 1) return 1;
    if (count === 2) return 2;
    if (count === 3) return isDesktop ? 3 : 2;
    if (count === 4) return 2;
    return 2;
  })();

  const handleImageClick = (e, index) => {
    e.stopPropagation();
    if (onOpenViewer) onOpenViewer(imageUrls, index);
  };

  const handleClose = () => {
    if (submittingComment) return;
    if (onClose) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={isXs}
      fullWidth={!isXs}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: isXs ? "100vw" : 600,
          maxWidth: 600,
          height: isXs ? "100dvh" : "auto",
          m: 0,
          borderRadius: isXs ? 0 : 2,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 900,
          borderBottom: "1px solid rgba(0,0,0,0.06)",
          px: 1,
          py: 1.1,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <IconButton
          onClick={handleClose}
          size="small"
          aria-label="back"
          sx={{ ml: 0.5 }}
        >
          <KeyboardArrowLeftRoundedIcon />
        </IconButton>

        <Typography sx={{ fontWeight: 900 }}>投稿の詳細</Typography>

        <Box sx={{ flex: 1 }} />
      </DialogTitle>

      <DialogContent
        dividers
        onPointerDown={onSwipePointerDown}
        onPointerMove={onSwipePointerMove}
        onPointerUp={onSwipePointerUp}
        onPointerCancel={onSwipePointerCancel}
        sx={{
          p: 0,
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          touchAction: isXs ? "pan-y" : "auto",
        }}
      >
        {/* スクロール領域 */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: isXs ? 0 : 2,
            pt: 2,
            pb: 2,
          }}
        >
          {/* 投稿本体 */}
          <Box sx={{ mb: 2 }}>
            {/* 1行目：アイコン + 名前 + 時刻 */}
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Avatar
                src={post.userAvatar || ""}
                sx={{ cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onOpenProfile) {
                    onOpenProfile(post.userId, {
                      userName: post.userName,
                      avatar: post.userAvatar,
                    });
                  }
                }}
              >
                {(post.userName || "？").charAt(0)}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, mb: 0.3 }}
                >
                  {post.userName || "名無し"}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  {formatTimestamp(post.createdAt)}
                </Typography>
              </Box>
            </Stack>

            {/* 本文テキスト */}
            {post.text && (
              <Typography
                variant="body2"
                sx={{ mt: 1, whiteSpace: "pre-wrap" }}
              >
                {renderTextWithLinks(post.text)}
              </Typography>
            )}

            {/* URL プレビュー */}
            {showLinkPreview && (
              <Box
                component={MuiLink}
                href={post.linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                underline="none"
                onClick={(e) => e.stopPropagation()}
                sx={{
                  display: "block",
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "1px solid rgba(0,0,0,0.08)",
                  mt: 1.5,
                }}
              >
                <Stack direction="row" spacing={0}>
                  <Box
                    component="img"
                    src={post.linkMeta.imageUrl}
                    alt={post.linkMeta.title || post.linkUrl}
                    sx={{
                      width: 96,
                      height: 96,
                      objectFit: "cover",
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ p: 1.2, flex: 1, minWidth: 0 }}>
                    {post.linkMeta.title && (
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, mb: 0.5 }}
                        noWrap
                      >
                        {post.linkMeta.title}
                      </Typography>
                    )}
                    {post.linkMeta.description && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {post.linkMeta.description}
                      </Typography>
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        mt: 0.5,
                        display: "block",
                        opacity: 0.7,
                      }}
                      noWrap
                    >
                      {post.linkMeta.siteName ||
                        safeHostnameFromUrl(post.linkUrl)}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            )}

            {/* 画像群 */}
            {showImages && (
              <ImageList
                cols={cols}
                gap={1}
                sx={{
                  mt: 1.5,
                  borderRadius: 0,
                  overflow: "hidden",
                  maxWidth: 600,
                  mx: 0,
                }}
              >
                {imageUrls.map((url, idx) => (
                  <ImageListItem key={idx}>
                    <LazyImage
                      src={url}
                      alt={`post-${post.id}-${idx}`}
                      onClick={(e) => handleImageClick(e, idx)}
                      maxHeight={maxH}
                      style={{
                        width: "100%",
                        height: "auto",
                        objectFit: "cover",
                        cursor: "pointer",
                      }}
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            )}

            {/* いいね / コメント数 */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mt: 1 }}
            >
              <IconButton
                size="small"
                onClick={onToggleLike}
                disabled={!currentUser}
              >
                {post.isLiked ? (
                  <FavoriteRoundedIcon
                    sx={{ color: accent }}
                    fontSize="small"
                  />
                ) : (
                  <FavoriteBorderRoundedIcon fontSize="small" />
                )}
              </IconButton>
              <Typography variant="body2" sx={{ mr: 2 }}>
                {post.likeCount || 0}
              </Typography>

              <ChatBubbleOutlineRoundedIcon
                fontSize="small"
                sx={{ opacity: 0.7, mr: 0.5 }}
              />
              <Typography variant="body2">{commentCount}</Typography>
            </Stack>
          </Box>

          <Divider sx={{ mb: 1.5 }} />

          {/* コメント一覧 */}
          {loadingComments ? (
            <Box sx={{ textAlign: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : comments.length === 0 ? (
            <Typography
              variant="caption"
              sx={{
                opacity: 0.7,
                display: "block",
                textAlign: "center",
                py: 1,
              }}
            >
              まだコメントはありません。
            </Typography>
          ) : (
            <Stack spacing={1.2} sx={{ mb: 1 }}>
              {comments.map((c) => {
                const cImageUrls = Array.isArray(c.imageUrls)
                  ? c.imageUrls.map((img) =>
                      typeof img === "string" ? img : img.url
                    )
                  : [];

                const isMineComment =
                  currentUser && c.userId === currentUser.uid;

                return (
                  <Stack
                    key={c.id}
                    direction="row"
                    spacing={1}
                    alignItems="flex-start"
                  >
                    <Avatar
                      src={c.userAvatar || ""}
                      sx={{ width: 28, height: 28, mt: 0.2 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onOpenProfile) {
                          onOpenProfile(c.userId, {
                            userName: c.userName,
                            avatar: c.userAvatar,
                          });
                        }
                      }}
                    >
                      {(c.userName || "？").charAt(0)}
                    </Avatar>
                    <Box
                      sx={{
                        flex: 1,
                        px: 1,
                        py: 0.6,
                        borderRadius: 2,
                        bgcolor: theme.appBg || "rgba(0,0,0,0.02)",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="baseline"
                          spacing={0.5}
                          sx={{ flex: 1, minWidth: 0 }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 700 }}
                          >
                            {c.userName || "名無し"}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.6 }}>
                            {formatTimestamp(c.createdAt)}
                          </Typography>
                        </Stack>
                        {isMineComment && (
                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenCommentMenu(e, c)}
                          >
                            <MoreVertRoundedIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                      {c.text && (
                        <Typography
                          variant="body2"
                          sx={{ mt: 0.2, whiteSpace: "pre-wrap" }}
                        >
                          {renderTextWithLinks(c.text)}
                        </Typography>
                      )}

                      {/* コメント画像 */}
                      {cImageUrls.length > 0 && (
                        <ImageList
                          cols={cImageUrls.length === 1 ? 1 : 2}
                          gap={1}
                          sx={{
                            mt: 0.6,
                            borderRadius: 1.5,
                            overflow: "hidden",
                            mx: -1,
                            width: "calc(100% + 16px)",
                          }}
                        >
                          {cImageUrls.map((url, idx) => (
                            <ImageListItem key={idx}>
                              <LazyImage
                                src={url}
                                alt={`comment-${c.id}-${idx}`}
                                maxHeight={IMAGE_MAX_HEIGHT}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (onOpenViewer)
                                    onOpenViewer(cImageUrls, idx);
                                }}
                                style={{
                                  width: "100%",
                                  height: "auto",
                                  objectFit: "cover",
                                  cursor: "pointer",
                                }}
                              />
                            </ImageListItem>
                          ))}
                        </ImageList>
                      )}

                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.5}
                        sx={{ mt: 0.3 }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleToggleCommentLike(c)}
                          disabled={!currentUser}
                        >
                          {c.isLiked ? (
                            <FavoriteRoundedIcon
                              sx={{ color: accent }}
                              fontSize="small"
                            />
                          ) : (
                            <FavoriteBorderRoundedIcon fontSize="small" />
                          )}
                        </IconButton>
                        <Typography variant="caption">
                          {c.likeCount || 0}
                        </Typography>
                      </Stack>
                    </Box>
                  </Stack>
                );
              })}
            </Stack>
          )}

          {/* コメント削除メニュー */}
          <Menu
            anchorEl={commentMenuAnchor}
            open={Boolean(commentMenuAnchor)}
            onClose={handleCloseCommentMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{
              sx: {
                minWidth: 160,
                borderRadius: 2,
                boxShadow: "0 10px 30px rgba(15,10,40,0.25)",
              },
            }}
          >
            <MenuItem
              onClick={(e) => {
                e.stopPropagation();
                handleOpenCommentDeleteDialog();
              }}
            >
              <ListItemIcon>
                <DeleteOutlineRoundedIcon fontSize="small" />
              </ListItemIcon>
              コメントを削除
            </MenuItem>
          </Menu>

          {/* コメント削除確認ダイアログ */}
          <Dialog
            open={commentDeleteDialogOpen}
            onClose={handleCancelCommentDelete}
            fullWidth
            maxWidth="xs"
          >
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <WarningAmberRoundedIcon
                sx={{ color: "warning.main", fontSize: 24 }}
              />
              このコメントを削除しますか？
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ mb: 2 }}>
                この操作は取り消せません。消去したコメントはもとに戻せません。
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleCancelCommentDelete}
                disabled={deletingComment}
              >
                キャンセル
              </Button>
              <Button
                onClick={handleConfirmCommentDelete}
                color="error"
                variant="contained"
                disabled={deletingComment}
              >
                {deletingComment ? "削除中..." : "削除する"}
              </Button>
            </DialogActions>
          </Dialog>
        </Box>

        {/* コメントフォーム（下に張り付け） */}
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            bgcolor: surface,
            borderTop: "1px solid rgba(0,0,0,0.06)",
            px: 2,
            py: 1,
            zIndex: 1,
          }}
        >
          {currentUser && (
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Avatar
                src={userProfile?.avatar || currentUser.photoURL || ""}
                sx={{ width: 28, height: 28, mt: 0.5 }}
              >
                {(
                  userProfile?.name ||
                  userProfile?.registrationName ||
                  currentUser.displayName ||
                  "？"
                ).charAt(0)}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <TextField
                  multiline
                  fullWidth
                  minRows={1}
                  maxRows={3}
                  placeholder="コメント（88文字まで）"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  inputProps={{ maxLength: 176 }}
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "transparent",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "transparent",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "transparent",
                    },
                    bgcolor: "rgba(0,0,0,0.02)",
                    borderRadius: 2,
                    px: 1,
                    py: 0.3,
                  }}
                />

                {/* 画像ボタン + プレビュー + 文字数 + 送信 */}
                <Stack sx={{ mt: 0.5, width: "100%" }} spacing={0.5}>
                  <Button
                    component="label"
                    variant="text"
                    size="small"
                    startIcon={<ImageRoundedIcon fontSize="small" />}
                    sx={{ whiteSpace: "nowrap", alignSelf: "flex-start" }}
                  >
                    画像
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      multiple
                      onChange={handleSelectCommentFiles}
                    />
                  </Button>

                  {commentFiles.length > 0 && (
                    <Box
                      sx={{
                        width: "100%",
                        maxWidth: "100%",
                        overflowX: "auto",
                        overflowY: "hidden",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        p: 0.5,
                        borderRadius: 1.5,
                        bgcolor: "rgba(0,0,0,0.02)",
                      }}
                    >
                      {commentFiles.map((cf, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            position: "relative",
                            width: 56,
                            height: 56,
                            borderRadius: 1.5,
                            overflow: "hidden",
                            border: "1px solid rgba(0,0,0,0.08)",
                            flexShrink: 0,
                          }}
                        >
                          <img
                            src={cf.previewUrl}
                            alt={`comment-preview-${idx}`}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              display: "block",
                            }}
                          />
                          <Stack
                            direction="row"
                            spacing={0.2}
                            sx={{
                              position: "absolute",
                              right: 2,
                              bottom: 2,
                            }}
                          >
                            <IconButton
                              size="small"
                              sx={{
                                width: 18,
                                height: 18,
                                bgcolor: "rgba(0,0,0,0.45)",
                                "&:hover": {
                                  bgcolor: "rgba(0,0,0,0.7)",
                                },
                              }}
                              disabled={idx === 0}
                              onClick={() => moveCommentFile(idx, -1)}
                            >
                              <KeyboardArrowLeftRoundedIcon
                                sx={{ fontSize: 14, color: "#fff" }}
                              />
                            </IconButton>
                            <IconButton
                              size="small"
                              sx={{
                                width: 18,
                                height: 18,
                                bgcolor: "rgba(0,0,0,0.45)",
                                "&:hover": {
                                  bgcolor: "rgba(0,0,0,0.7)",
                                },
                              }}
                              disabled={idx === commentFiles.length - 1}
                              onClick={() => moveCommentFile(idx, 1)}
                            >
                              <KeyboardArrowRightRoundedIcon
                                sx={{ fontSize: 14, color: "#fff" }}
                              />
                            </IconButton>
                          </Stack>
                        </Box>
                      ))}
                    </Box>
                  )}

                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="flex-end"
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: commentTextOver
                          ? "error.main"
                          : "text.secondary",
                      }}
                    >
                      {commentTextCount}/88
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={handleCreateComment}
                      disabled={
                        submittingComment ||
                        (!commentText.trim() && commentFiles.length === 0) ||
                        commentTextOver
                      }
                    >
                      <SendRoundedIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>
              </Box>
            </Stack>
          )}

          {submittingComment && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                bgcolor: "rgba(255,255,255,0.7)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
              }}
            >
              <CircularProgress size={32} />
              <Typography variant="body2" sx={{ mt: 1.5 }}>
                コメントを投稿しています…
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

// ==========================================================
// 🧩 プロフィールダイアログ（右からスライド）
//   - 最近の投稿：10件ずつ無限ロード
//   - 詳細→戻る でスクロール位置/読み込み状態を維持
// ==========================================================

const ProfileTransition = React.forwardRef(function ProfileTransition(
  props,
  ref
) {
  return <Slide direction="left" ref={ref} {...props} />;
});

function ProfileDialog({
  open,
  onClose,
  user,
  loading,
  theme,
  currentUser,
  currentUserProfile,
  onOpenViewer,
}) {
  const surface = theme.surfaceBg || "#FFFFFF";
  const accent = theme.railActive || "#5A35FF";
  const textBase = theme.railText || "rgba(30,30,60,0.9)";
  const isXs = useMediaQuery("(max-width:600px)");

  const displayName =
    user?.name ||
    user?.registrationName ||
    user?.userName ||
    "プロフィール未登録";
  const avatarUrl = user?.avatar || user?.userAvatar || "";
  const prefecture = user?.prefecture || user?.pref || user?.region || null;
  const bio =
    user?.bio || user?.profileMessage || user?.selfIntroduction || null;

  // ─────────────────────────────────────────────
  // 画面状態（profile → post）
  // ─────────────────────────────────────────────
  const [view, setView] = useState("profile"); // "profile" | "post"
  const [selectedPost, setSelectedPost] = useState(null);

  // 最近の投稿（縦・無限スクロール：10件ずつ）
  const [recentPosts, setRecentPosts] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [recentLoadingMore, setRecentLoadingMore] = useState(false);
  const [recentLastVisible, setRecentLastVisible] = useState(null);
  const [recentHasMore, setRecentHasMore] = useState(false);

  // ★ ここが肝：プロフィール内スクロールと位置保存
  const profileScrollRef = useRef(null);
  const savedProfileScrollTopRef = useRef(0);

  // ★ IntersectionObserver のターゲット
  const recentSentinelRef = useRef(null);

  // 投稿詳細（コメント購読）
  const [postComments, setPostComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);

  const [postBusy, setPostBusy] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);

  const [ddpText, setDdpText] = useState("");
  const [ddpDisplayLoading, setDdpDisplayLoading] = useState(false);

  // ─────────────────────────────────────────────
  // open が閉じられた時だけ全リセット（維持したいのは open の間）
  // ─────────────────────────────────────────────
  useEffect(() => {
    if (!open) {
      setView("profile");
      setSelectedPost(null);

      setRecentPosts([]);
      setRecentLastVisible(null);
      setRecentHasMore(false);
      setRecentLoading(false);
      setRecentLoadingMore(false);

      setPostComments([]);
      setCommentText("");

      savedProfileScrollTopRef.current = 0;
    }
  }, [open]);

  // ✅ ここに入れる（ddpText / ddpDisplayLoading の定義の直後がベスト）
  useEffect(() => {
    if (!open) return;
    if (!user?.id) return;

    // 公開OFFなら何も表示しない（DDP本文も消す）
    if (!user?.ddpProfileEnabled) {
      setDdpText("");
      setDdpDisplayLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setDdpDisplayLoading(true);
      try {
        const colRef = collection(db, "users", user.id, "ddps");
        const snap = await getDocs(
          query(colRef, orderBy("updatedAt", "desc"), limit(1))
        );

        if (cancelled) return;

        if (!snap.empty) {
          const data = snap.docs[0].data();
          setDdpText(String(data.ddp || ""));
        } else {
          setDdpText("");
        }
      } catch (e) {
        console.error("Failed to load DDP for profile:", e);
        if (!cancelled) setDdpText("");
      } finally {
        if (!cancelled) setDdpDisplayLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [open, user?.id, user?.ddpProfileEnabled]);

  // ─────────────────────────────────────────────
  // user が変わったら、別人のプロフィールになるので初期化
  // ─────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;

    setView("profile");
    setSelectedPost(null);

    setRecentPosts([]);
    setRecentLastVisible(null);
    setRecentHasMore(false);
    setRecentLoading(false);
    setRecentLoadingMore(false);

    setPostComments([]);
    setCommentText("");

    savedProfileScrollTopRef.current = 0;
  }, [open, user?.id]);

  useEffect(() => {
    if (!open) return;
    console.log("[ProfileDialog user keys]", Object.keys(user || {}));
    console.log("[ProfileDialog selfIntroduction]", user?.selfIntroduction);
  }, [open]);
  

  const firstThumbFromPost = (p) => {
    const arr = Array.isArray(p?.imageUrls) ? p.imageUrls : [];
    if (arr.length === 0) return null;
    const first = arr[0];
    return typeof first === "string" ? first : first?.url || null;
  };

  // ─────────────────────────────────────────────
  // 最近の投稿：初回取得（※すでに取得済みなら何もしない）
  // ─────────────────────────────────────────────
  const fetchRecentInitial = async () => {
    if (!open || !user?.id) return;
    if (recentLoading || recentLoadingMore) return;
    if (recentPosts.length > 0) return; // ★ 重要：保持しているので再取得しない

    setRecentLoading(true);
    try {
      const postsRef = collection(db, "mmPosts");

      const q = query(
        postsRef,
        where("userId", "==", user.id),
        orderBy("createdAt", "desc"),
        limit(RECENT_PAGE_SIZE)
      );

      const snap = await getDocs(q);

      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setRecentPosts(list);

      const lastDoc = snap.docs[snap.docs.length - 1] || null;
      setRecentLastVisible(lastDoc);
      setRecentHasMore(snap.size === RECENT_PAGE_SIZE);
    } catch (e) {
      console.error("Failed to load recent posts:", e);
      setRecentPosts([]);
      setRecentLastVisible(null);
      setRecentHasMore(false);
    } finally {
      setRecentLoading(false);
    }
  };

  const loadMoreRecent = async () => {
    if (!open || !user?.id) return;
    if (!recentLastVisible) return;
    if (!recentHasMore) return;
    if (recentLoadingMore) return;

    setRecentLoadingMore(true);
    try {
      const postsRef = collection(db, "mmPosts");

      const q = query(
        postsRef,
        where("userId", "==", user.id),
        orderBy("createdAt", "desc"),
        startAfter(recentLastVisible),
        limit(RECENT_PAGE_SIZE)
      );

      const snap = await getDocs(q);

      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setRecentPosts((prev) => [...prev, ...list]);

      const lastDoc = snap.docs[snap.docs.length - 1] || null;
      setRecentLastVisible(lastDoc);
      setRecentHasMore(snap.size === RECENT_PAGE_SIZE);
    } catch (e) {
      console.error("Failed to load more recent posts:", e);
    } finally {
      setRecentLoadingMore(false);
    }
  };

  // プロフィール表示に入ったときに初回ロード（保持されていれば何もしない）
  useEffect(() => {
    if (!open || !user?.id) return;
    if (view !== "profile") return;
    fetchRecentInitial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user?.id, view]);

  // ─────────────────────────────────────────────
  // 最近の投稿：無限スクロール
  // ★ root を profileScrollRef にする（ダイアログ内スクロールに追従）
  // ─────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    if (view !== "profile") return;
    if (!recentHasMore) return;

    const rootEl = profileScrollRef.current;
    const target = recentSentinelRef.current;
    if (!rootEl || !target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) loadMoreRecent();
      },
      {
        root: rootEl, // ★重要
        rootMargin: "240px",
        threshold: 0.01,
      }
    );

    observer.observe(target);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, view, recentHasMore, recentLastVisible, recentLoadingMore]);

  // ─────────────────────────────────────────────
  // 投稿詳細：コメント購読
  // ─────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    if (view !== "post") return;
    if (!selectedPost?.id) return;

    setLoadingComments(true);

    const commentsRef = collection(db, "mmPosts", selectedPost.id, "comments");
    const q = query(commentsRef, orderBy("createdAt", "desc"), limit(100));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => {
          const data = d.data();
          const likes = data.likes || {};
          const isLiked = currentUser ? !!likes[currentUser.uid] : false;
          return { id: d.id, ...data, isLiked };
        });
        setPostComments(list);
        setLoadingComments(false);
      },
      (err) => {
        console.error("Failed to load post comments in profile:", err);
        setPostComments([]);
        setLoadingComments(false);
      }
    );

    return () => unsub();
  }, [open, view, selectedPost?.id, currentUser?.uid]);

  // ─────────────────────────────────────────────
  // 「最近の投稿」→投稿詳細へ
  // ★ ここでスクロール位置を保存
  // ─────────────────────────────────────────────
  const handleOpenPost = (p) => {
    if (!p) return;

    if (profileScrollRef.current) {
      savedProfileScrollTopRef.current =
        profileScrollRef.current.scrollTop || 0;
    }

    const likes = p.likes || {};
    const isLiked = currentUser ? !!likes[currentUser.uid] : false;

    setSelectedPost({ ...p, isLiked });
    setView("post");
  };

  // ─────────────────────────────────────────────
  // 投稿詳細 → プロフィールへ戻る
  // ★ 描画後にスクロール復元（100番目の位置に戻る）
  // ─────────────────────────────────────────────
  const handleBackToProfile = () => {
    setView("profile");
    setSelectedPost(null);
    setPostComments([]);
    setCommentText("");

    requestAnimationFrame(() => {
      const el = profileScrollRef.current;
      if (el) el.scrollTop = savedProfileScrollTopRef.current || 0;
    });
  };

  const handleClose = () => {
    if (onClose) onClose();
  };

  // ─────────────────────────────────────────────
  // いいね（プロフィール内詳細）
  // ─────────────────────────────────────────────
  const toggleSelectedPostLike = async () => {
    if (!currentUser || !selectedPost?.id || postBusy) return;

    const newIsLiked = !selectedPost.isLiked;
    const delta = newIsLiked ? 1 : -1;

    setSelectedPost((prev) => ({
      ...prev,
      isLiked: newIsLiked,
      likeCount: (prev.likeCount || 0) + delta,
    }));

    setPostBusy(true);
    try {
      const postRef = doc(db, "mmPosts", selectedPost.id);
      const field = `likes.${currentUser.uid}`;

      if (newIsLiked) {
        await updateDoc(postRef, {
          [field]: true,
          likeCount: increment(1),
        });
      } else {
        await updateDoc(postRef, {
          [field]: deleteField(),
          likeCount: increment(-1),
        });
      }
    } catch (e) {
      console.error("Failed to toggle post like:", e);
      setSelectedPost((prev) => ({
        ...prev,
        isLiked: !newIsLiked,
        likeCount: (prev.likeCount || 0) - delta,
      }));
    } finally {
      setPostBusy(false);
    }
  };

  const commentOver = commentText.length > 88;

  const submitCommentInProfile = async () => {
    if (!currentUser || !selectedPost?.id) return;
    if (!commentText.trim()) return;
    if (commentOver) return;

    setSubmittingComment(true);
    try {
      const displayName =
        currentUserProfile?.name ||
        currentUserProfile?.registrationName ||
        currentUser.displayName ||
        "名無し";

      const avatarUrl =
        currentUserProfile?.avatar || currentUser.photoURL || "";

      const commentsRef = collection(
        db,
        "mmPosts",
        selectedPost.id,
        "comments"
      );
      await addDoc(commentsRef, {
        userId: currentUser.uid,
        userName: displayName,
        userAvatar: avatarUrl,
        text: commentText.trim(),
        imageUrls: [],
        likeCount: 0,
        likes: {},
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const postRef = doc(db, "mmPosts", selectedPost.id);
      await updateDoc(postRef, { commentCount: increment(1) });

      setSelectedPost((prev) => ({
        ...prev,
        commentCount: (prev.commentCount ?? 0) + 1,
      }));

      setCommentText("");
    } catch (e) {
      console.error("Failed to submit comment in profile:", e);
    } finally {
      setSubmittingComment(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      TransitionComponent={ProfileTransition}
      keepMounted
      fullScreen={isXs}
      fullWidth={!isXs}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: isXs ? "100vw" : 600,
          maxWidth: 600,
          height: isXs ? "100dvh" : "auto",
          m: 0,
          bgcolor: surface,
          borderRadius: isXs ? 0 : 2,
          boxShadow: "0 18px 45px rgba(15,10,40,0.35)",
          overflow: "hidden",
        },
      }}
    >
      {/* ヘッダー */}
      <DialogTitle
        sx={{
          borderBottom: "1px solid rgba(0,0,0,0.06)",
          fontWeight: 900,
          px: 2,
          py: 1.25,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        {view === "post" ? (
          <IconButton onClick={handleBackToProfile} size="small">
            <KeyboardArrowLeftRoundedIcon />
          </IconButton>
        ) : (
          <Box sx={{ width: 36 }} />
        )}

        <Typography sx={{ fontWeight: 900 }}>
          {view === "post" ? "投稿の詳細" : "プロフィール"}
        </Typography>

        <Box sx={{ flex: 1 }} />

        <Button onClick={handleClose} size="small">
          とじる
        </Button>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          height: "calc(100dvh - 56px)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* =========================
              PROFILE VIEW
             ========================= */}
        <Box
          ref={profileScrollRef}
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 2,
            pt: 2,
            pb: 2.5,
            display: view === "profile" ? "block" : "none",
          }}
        >
          {loading ? (
            <Box
              sx={{
                py: 4,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
              }}
            >
              <CircularProgress size={28} />
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                プロフィールを読み込み中…
              </Typography>
            </Box>
          ) : (
            <>
              {/* アイコン＋名前＋都道府県 */}
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Avatar
                  src={avatarUrl}
                  sx={{
                    width: 64,
                    height: 64,
                    boxShadow: "0 8px 20px rgba(0,0,0,0.2)",
                  }}
                >
                  {displayName.charAt(0)}
                </Avatar>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 900, color: textBase }}
                    noWrap
                  >
                    {displayName}
                  </Typography>

                  {prefecture && (
                    <Chip
                      label={prefecture}
                      size="small"
                      sx={{
                        mt: 0.6,
                        bgcolor: "rgba(0,0,0,0.04)",
                        fontWeight: 700,
                      }}
                    />
                  )}
                </Box>
              </Stack>

              {/* 自己紹介 */}
              <Box
                sx={{
                  borderRadius: 2,
                  bgcolor: "rgba(0,0,0,0.02)",
                  p: 1.5,
                  mb: 2,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 900,
                    opacity: 0.7,
                    mb: 0.6,
                    display: "block",
                  }}
                >
                  ひとこと
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "pre-wrap",
                    minHeight: 40,
                    opacity: bio ? 1 : 0.6,
                  }}
                >
                  {bio || "自己紹介メッセージはまだ登録されていません。"}
                </Typography>
              </Box>

              {/* DDP（本人が公開ONにしている場合のみ） */}
              {user?.ddpProfileEnabled && (
                <Box
                  sx={{
                    borderRadius: 2,
                    bgcolor: "rgba(0,0,0,0.02)",
                    p: 1.5,
                    mb: 2,
                    border: "1px solid rgba(0,0,0,0.06)",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 900,
                      opacity: 0.7,
                      mb: 0.6,
                      display: "block",
                    }}
                  >
                    DDP
                  </Typography>

                  {ddpDisplayLoading ? (
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ py: 0.5 }}
                    >
                      <CircularProgress size={18} />
                      <Typography variant="body2" sx={{ opacity: 0.7 }}>
                        DDPを読み込み中…
                      </Typography>
                    </Stack>
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: "pre-wrap",
                        opacity: ddpText ? 1 : 0.65,
                        lineHeight: 1.8,
                      }}
                    >
                      {ddpText || "DDPはまだ登録されていません。"}
                    </Typography>
                  )}
                </Box>
              )}

              {/* 最近の投稿 */}
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 900, mb: 1, color: textBase }}
              >
                最近の投稿
              </Typography>

              {recentLoading ? (
                <Box sx={{ py: 2, textAlign: "center" }}>
                  <CircularProgress size={22} />
                </Box>
              ) : recentPosts.length === 0 ? (
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                  まだ投稿はありません。
                </Typography>
              ) : (
                <>
                  <Stack spacing={1.2}>
                    {recentPosts.map((p) => (
                      <RecentPostRow
                        key={p.id}
                        post={p}
                        accent={accent}
                        onOpen={() => handleOpenPost(p)}
                        getThumb={firstThumbFromPost}
                      />
                    ))}
                  </Stack>

                  {/* sentinel（末尾が見えたら次10件） */}
                  <Box
                    ref={recentSentinelRef}
                    sx={{ height: recentHasMore ? 36 : 0 }}
                  />

                  {recentLoadingMore && (
                    <Box sx={{ textAlign: "center", py: 2 }}>
                      <CircularProgress size={22} />
                    </Box>
                  )}

                  {!recentHasMore &&
                    !recentLoadingMore &&
                    recentPosts.length > 0 && (
                      <Box sx={{ textAlign: "center", py: 1 }}>
                        <Typography variant="caption" sx={{ opacity: 0.6 }}>
                          すべて読み込みました
                        </Typography>
                      </Box>
                    )}
                </>
              )}
            </>
          )}
        </Box>

        {/* =========================
              POST DETAIL VIEW
             ========================= */}
        <Box
          sx={{
            flex: 1,
            display: view === "post" ? "flex" : "none",
            flexDirection: "column",
            height: "calc(100dvh - 56px)",
            overflow: "hidden",
          }}
        >
          {/* 上：スクロール */}
          <Box sx={{ flex: 1, overflowY: "auto", px: 2, pt: 2, pb: 2 }}>
            {!selectedPost ? (
              <Box sx={{ py: 3, textAlign: "center" }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Box>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <Avatar src={selectedPost.userAvatar || avatarUrl || ""}>
                    {(selectedPost.userName || displayName || "？").charAt(0)}
                  </Avatar>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                      {selectedPost.userName || displayName || "名無し"}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7 }}>
                      {formatTimestamp(selectedPost.createdAt)}
                    </Typography>
                  </Box>
                </Stack>

                {selectedPost.text && (
                  <Typography variant="body2" sx={{ mt: 1.2 }}>
                    {renderTextWithLinks(selectedPost.text)}
                  </Typography>
                )}

                {selectedPost.linkUrl &&
                  selectedPost.linkMeta &&
                  selectedPost.linkMeta.imageUrl && (
                    <Box
                      component={MuiLink}
                      href={selectedPost.linkUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="none"
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        display: "block",
                        borderRadius: 2,
                        overflow: "hidden",
                        border: "1px solid rgba(0,0,0,0.08)",
                        mt: 1.5,
                      }}
                    >
                      <Stack direction="row" spacing={0}>
                        <Box
                          component="img"
                          src={selectedPost.linkMeta.imageUrl}
                          alt={
                            selectedPost.linkMeta.title || selectedPost.linkUrl
                          }
                          sx={{
                            width: 96,
                            height: 96,
                            objectFit: "cover",
                            flexShrink: 0,
                          }}
                        />
                        <Box sx={{ p: 1.2, flex: 1, minWidth: 0 }}>
                          {selectedPost.linkMeta.title && (
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 800, mb: 0.5 }}
                              noWrap
                            >
                              {selectedPost.linkMeta.title}
                            </Typography>
                          )}
                          {selectedPost.linkMeta.description && (
                            <Typography
                              variant="caption"
                              sx={{
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {selectedPost.linkMeta.description}
                            </Typography>
                          )}
                          <Typography
                            variant="caption"
                            sx={{ mt: 0.5, display: "block", opacity: 0.7 }}
                            noWrap
                          >
                            {selectedPost.linkMeta.siteName ||
                              safeHostnameFromUrl(selectedPost.linkUrl)}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  )}

                {Array.isArray(selectedPost?.imageUrls) &&
                  selectedPost.imageUrls.length > 0 &&
                  (() => {
                    const selectedImageUrls = selectedPost.imageUrls
                      .map((img) => (typeof img === "string" ? img : img?.url))
                      .filter(Boolean);

                    const count = selectedImageUrls.length;
                    const maxH = getImageMaxHeight(count);
                    const cols = count === 1 ? 1 : count === 2 ? 2 : 2;

                    return (
                      <ImageList
                        cols={cols}
                        gap={1}
                        sx={{
                          mt: 1.5,
                          borderRadius: 2,
                          overflow: "hidden",
                          maxWidth: 520,
                          mx: "auto",
                        }}
                      >
                        {selectedImageUrls.map((url, idx) => (
                          <ImageListItem key={idx}>
                            <LazyImage
                              src={url}
                              alt={`profile-post-${selectedPost.id}-${idx}`}
                              maxHeight={maxH}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (onOpenViewer)
                                  onOpenViewer(selectedImageUrls, idx);
                              }}
                              style={{
                                width: "100%",
                                height: "auto",
                                objectFit: "cover",
                                cursor: "pointer",
                                display: "block",
                              }}
                            />
                          </ImageListItem>
                        ))}
                      </ImageList>
                    );
                  })()}
              </Box>
            )}

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mt: 1.2 }}
            >
              <IconButton
                size="small"
                onClick={toggleSelectedPostLike}
                disabled={!currentUser || postBusy}
              >
                {selectedPost?.isLiked ? (
                  <FavoriteRoundedIcon
                    sx={{ color: accent }}
                    fontSize="small"
                  />
                ) : (
                  <FavoriteBorderRoundedIcon fontSize="small" />
                )}
              </IconButton>
              <Typography variant="body2">
                {selectedPost?.likeCount || 0}
              </Typography>

              <ChatBubbleOutlineRoundedIcon
                sx={{ fontSize: 18, opacity: 0.65, ml: 1 }}
              />
              <Typography variant="body2">
                {selectedPost?.commentCount ?? 0}
              </Typography>
            </Stack>

            <Divider sx={{ my: 2 }} />

            {loadingComments ? (
              <Box sx={{ textAlign: "center", py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : postComments.length === 0 ? (
              <Typography
                variant="caption"
                sx={{ opacity: 0.7, display: "block" }}
              >
                まだコメントはありません。
              </Typography>
            ) : (
              <Stack spacing={1} sx={{ pb: 1 }}>
                {postComments.map((c) => (
                  <Stack
                    key={c.id}
                    direction="row"
                    spacing={1}
                    alignItems="flex-start"
                  >
                    <Avatar
                      src={c.userAvatar || ""}
                      sx={{ width: 28, height: 28, mt: 0.2 }}
                    >
                      {(c.userName || "？").charAt(0)}
                    </Avatar>

                    <Box
                      sx={{
                        flex: 1,
                        px: 1,
                        py: 0.75,
                        borderRadius: 2,
                        bgcolor: theme.appBg || "rgba(0,0,0,0.03)",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="baseline">
                        <Typography variant="caption" sx={{ fontWeight: 900 }}>
                          {c.userName || "名無し"}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.6 }}>
                          {formatTimestamp(c.createdAt)}
                        </Typography>
                      </Stack>

                      {c.text && (
                        <Typography variant="body2" sx={{ mt: 0.3 }}>
                          {renderTextWithLinks(c.text)}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                ))}
              </Stack>
            )}
          </Box>

          {/* 下：コメント投稿（固定） */}
          <Box
            sx={{
              borderTop: "1px solid rgba(0,0,0,0.06)",
              bgcolor: surface,
              px: 2,
              py: 1,
            }}
          >
            {currentUser ? (
              <Stack direction="row" spacing={1} alignItems="flex-end">
                <Avatar
                  src={currentUserProfile?.avatar || currentUser.photoURL || ""}
                  sx={{ width: 28, height: 28, mb: 0.4 }}
                />
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={1}
                    maxRows={3}
                    placeholder="コメント（88文字まで）"
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    inputProps={{ maxLength: 176 }}
                    sx={{
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "transparent",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "transparent",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "transparent",
                      },
                      bgcolor: "rgba(0,0,0,0.03)",
                      borderRadius: 2,
                    }}
                  />
                  <Stack
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="center"
                    spacing={1}
                    sx={{ mt: 0.5 }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: commentOver ? "error.main" : "text.secondary",
                      }}
                    >
                      {commentText.length}/88
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={submitCommentInProfile}
                      disabled={
                        submittingComment || !commentText.trim() || commentOver
                      }
                    >
                      <SendRoundedIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Box>
              </Stack>
            ) : (
              <Typography variant="caption" sx={{ opacity: 0.7 }}>
                コメントするにはログインが必要です。
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

/* ==========================================================
    🧩 プロフィール「最近の投稿」1行カード（縦リスト用）
   ========================================================== */
function RecentPostRow({ post, accent, onOpen, getThumb }) {
  const thumb = getThumb(post);
  const text = (post.text || "").trim();

  return (
    <Card
      elevation={0}
      onClick={onOpen}
      sx={{
        borderRadius: 2,
        border: "1px solid rgba(0,0,0,0.10)",
        overflow: "hidden",
        cursor: "pointer",
        "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
      }}
    >
      <CardContent sx={{ py: 1.2, px: 1.2 }}>
        <Stack direction="row" spacing={1.2} alignItems="center">
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: 1.5,
              overflow: "hidden",
              flexShrink: 0,
              bgcolor: "rgba(0,0,0,0.03)",
              border: "1px solid rgba(0,0,0,0.06)",
            }}
          >
            {thumb ? (
              <LazyImage
                src={thumb}
                alt="thumb"
                maxHeight={72}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  No Image
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" sx={{ opacity: 0.65 }}>
              {formatTimestamp(post.createdAt)}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                mt: 0.4,
                fontWeight: 900,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                whiteSpace: "pre-wrap",
              }}
            >
              {text || "（画像投稿）"}
            </Typography>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mt: 0.6 }}
            >
              <FavoriteRoundedIcon
                sx={{ fontSize: 16, color: accent, opacity: 0.65 }}
              />
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                {post.likeCount || 0}
              </Typography>
              <ChatBubbleOutlineRoundedIcon
                sx={{ fontSize: 16, opacity: 0.6, ml: 1 }}
              />
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                {post.commentCount ?? 0}
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
